/**
 * What a request looks like while it is with the processor and after: on a row, the one shared line "Sent to the
 * scheduler · answers in a few minutes" (./WithScheduler — clock + text, never colour alone, never a spinner that
 * blocks), then the processor's OutcomeMessage exactly as it wrote it. Recent requests lists the last 20 this
 * device sent, with their outcome (there, and beside a sentence that says what is waiting, the short Pending badge).
 */
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { openPresses } from '@shared/with-scheduler';
import { devicePresses, txKey, useTx, type TrackedTx } from '@/lib/tx';
import { formatInstant } from '@/lib/dates';
import { Icon } from './Icons';
import { OutcomeGloss } from './OutcomeGloss';
import { Button } from './ui/button';
import { Sheet, SheetContent } from './ui/sheet';
import { WithScheduler } from './WithScheduler';

/** The request is with the processor. */
export function PendingBadge({ tx }: { tx: TrackedTx }) {
  const { t } = useTranslation();
  return (
    <span className="status sched has-icon" data-testid="pending-badge" title={tx.label}>
      <Icon name="clock" />
      {t('tx.pending')}
    </span>
  );
}

/** As much of a closed request as its line needs (a tracked request, or the server's word of a refusal). */
type Outcome = Pick<TrackedTx, 'processingStatus' | 'outcomeCode' | 'outcomeMessage'>;

function outcomeTone(tx: Outcome): 'ok' | 'no' {
  return tx.processingStatus === 'Completed' && (tx.outcomeCode === 'Applied' || tx.outcomeCode === 'Duplicate Ignored') ? 'ok' : 'no';
}

/** The processor's own words on the row, verbatim. */
export function OutcomeLine({ tx, testid = 'outcome' }: { tx: Outcome; testid?: string }) {
  const { t } = useTranslation();
  const ok = outcomeTone(tx) === 'ok';
  return (
    <span className={`outcome ${ok ? 'ok' : 'no'}`} data-testid={testid} data-code={tx.outcomeCode ?? ''}>
      <Icon name={ok ? 'check' : 'alert'} />
      <span className="sr-only">{ok ? t('tx.applied') : t('tx.refused')}: </span>
      <span lang={tx.outcomeMessage ? 'en' : undefined}>{tx.outcomeMessage ?? t('tx.closedNoMessage', { status: tx.processingStatus })}</span>
      <OutcomeGloss code={tx.outcomeCode} />
    </span>
  );
}

/**
 * "Sent to the scheduler · answers in a few minutes" while a request this device sent for the appointment is open;
 * the outcome once it closed (for half an hour). Today also has the server's list of open requests (pages/Today.tsx).
 */
export function TxOnRow({ appointmentId }: { appointmentId: string | null | undefined }) {
  const tx = useTx();
  const open = openPresses(appointmentId, null, devicePresses(tx.tracked))[0];
  if (open) return <WithScheduler at={open.at} />;
  const last = tx.lastFor(appointmentId);
  return last ? <OutcomeLine tx={last} /> : null;
}

export function RecentRequests() {
  const { t, i18n } = useTranslation();
  const { tracked } = useTx();
  const [open, setOpen] = useState(false);
  const opener = useRef<HTMLButtonElement>(null);
  const pending = tracked.filter((x) => x.open).length;
  return (
    <>
      <Button ref={opener} size="sm" variant="secondary" type="button" onClick={() => setOpen(true)} data-testid="recent-requests" aria-haspopup="dialog">
        <Icon name="clock" />
        {t('tx.recent')}
        {pending ? (
          <span className="count tnum" data-testid="recent-pending">
            {' · '}
            {t('tx.pendingCount', { count: pending })}
          </span>
        ) : null}
      </Button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent title={t('tx.recentTitle')} returnFocusTo={opener} data-testid="recent-sheet">
          {tracked.length === 0 ? (
            <p className="help">{t('tx.recentNone')}</p>
          ) : (
            <ul className="attn-list recent-list">
              {tracked.map((x) => (
                <li key={txKey(x)} data-testid="recent-item">
                  <div className="t">
                    <b>{x.label}</b>
                    <span className="help">
                      {t('tx.sentAt', { time: formatInstant(x.submittedAt || new Date(x.trackedAt).toISOString(), i18n.language) })}
                      {x.requestedBy ? ` · ${t(`tx.requestedBy.${x.requestedBy}`, { defaultValue: '' })}` : ''}
                    </span>
                    {x.open ? <PendingBadge tx={x} /> : <OutcomeLine tx={x} />}
                  </div>
                </li>
              ))}
            </ul>
          )}
          <p className="help">{t('tx.recentHelp')}</p>
        </SheetContent>
      </Sheet>
    </>
  );
}
